// Tremor Raw Popover [v0.0.0]

import * as PopoverPrimitives from '@radix-ui/react-popover'
import {
	forwardRef,
	type ElementRef,
	type ComponentPropsWithoutRef,
} from 'react'

import { cn } from '#app/utils/misc.tsx'

const Popover = (
	props: ComponentPropsWithoutRef<typeof PopoverPrimitives.Root>,
) => {
	return <PopoverPrimitives.Root {...props} />
}

Popover.displayName = 'Popover'

const PopoverTrigger = forwardRef<
	ElementRef<typeof PopoverPrimitives.Trigger>,
	ComponentPropsWithoutRef<typeof PopoverPrimitives.Trigger>
>((props, forwardedRef) => {
	return <PopoverPrimitives.Trigger ref={forwardedRef} {...props} />
})

PopoverTrigger.displayName = 'PopoverTrigger'

const PopoverAnchor = forwardRef<
	ElementRef<typeof PopoverPrimitives.Anchor>,
	ComponentPropsWithoutRef<typeof PopoverPrimitives.Anchor>
>((props, forwardedRef) => {
	return <PopoverPrimitives.Anchor ref={forwardedRef} {...props} />
})

PopoverAnchor.displayName = 'PopoverAnchor'

const PopoverClose = forwardRef<
	ElementRef<typeof PopoverPrimitives.Close>,
	ComponentPropsWithoutRef<typeof PopoverPrimitives.Close>
>((props, forwardedRef) => {
	return <PopoverPrimitives.Close ref={forwardedRef} {...props} />
})

PopoverClose.displayName = 'PopoverClose'

interface ContentProps
	extends ComponentPropsWithoutRef<typeof PopoverPrimitives.Content> {}

const PopoverContent = forwardRef<
	ElementRef<typeof PopoverPrimitives.Content>,
	ContentProps
>(
	(
		{
			className,
			sideOffset = 10,
			side = 'bottom',
			align = 'center',
			collisionPadding,
			avoidCollisions,
			...props
		}: ContentProps,
		forwardedRef,
	) => {
		return (
			<PopoverPrimitives.Portal>
				<PopoverPrimitives.Content
					data-slot="popover-content"
					ref={forwardedRef}
					sideOffset={sideOffset}
					side={side}
					align={align}
					collisionPadding={collisionPadding}
					avoidCollisions
					className={cn(
						// base
						'max-h-[var(--radix-popper-available-height)] min-w-60 overflow-hidden rounded-md border p-2.5 text-sm shadow-md',
						// border color
						'border-gray-200 dark:border-gray-800',
						// text color
						'text-gray-900 dark:text-gray-50',
						// background color
						'bg-white dark:bg-gray-950',
						// transition
						'will-change-[transform,opacity]',
						'data-[state=closed]:animate-hide',
						'data-[state=open]:data-[side=bottom]:animate-slideDownAndFade data-[state=open]:data-[side=left]:animate-slideLeftAndFade data-[state=open]:data-[side=right]:animate-slideRightAndFade data-[state=open]:data-[side=top]:animate-slideUpAndFade',
						// ensure above dialogs/sheets (z-50) - higher for modal popovers
						'z-[100]',

						className,
					)}
					{...props}
				/>
			</PopoverPrimitives.Portal>
		)
	},
)
PopoverContent.displayName = 'PopoverContent'

export { Popover, PopoverAnchor, PopoverClose, PopoverContent, PopoverTrigger }
